import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { client } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import StatusChip from '@/components/StatusChip';
import EditVendorDialog, { type EditableVendor } from '@/components/EditVendorDialog';
import { formatDate, formatInstant } from '@/lib/date';
import { formatQuote } from '@/lib/format';
import type { InquiryStatus } from '@vendorops/shared';

export default function VendorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [newEmail, setNewEmail] = useState('');
  const [editing, setEditing] = useState(false);

  const { data: vendor, isLoading } = useQuery({
    queryKey: ['vendor', id],
    queryFn: async () => {
      const res = await client.api.vendors[':id'].$get({ param: { id: id! } });
      if (!res.ok) throw new Error('Failed to load vendor');
      return res.json();
    },
    enabled: !!id,
  });

  const addEmail = useMutation({
    mutationFn: async (email: string) => {
      const res = await client.api.vendors[':id'].emails.$post({
        param: { id: id! },
        json: { email, isPrimary: false },
      });
      if (!res.ok) throw new Error('Failed to add address');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', id] });
      // The list page shows a vendor's primary address and caches for five
      // minutes, so it needs telling too.
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      setNewEmail('');
      toast.success('Address added');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const removeEmail = useMutation({
    mutationFn: async (emailId: string) => {
      const res = await client.api.vendors[':id'].emails[':emailId'].$delete({
        param: { id: id!, emailId },
      });
      if (!res.ok) throw new Error('Failed to remove address');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', id] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    },
  });

  const setArchived = useMutation({
    mutationFn: async (archived: boolean) => {
      const res = await client.api.vendors[':id'].$put({
        param: { id: id! },
        json: { archived },
      });
      if (!res.ok) throw new Error('Failed to update vendor');
      return res.json();
    },
    onSuccess: (_, archived) => {
      queryClient.invalidateQueries({ queryKey: ['vendor', id] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      toast.success(archived ? 'Vendor archived' : 'Vendor restored');
    },
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (!vendor) return <p className="text-sm text-muted-foreground">Vendor not found.</p>;

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <Link to="/vendors" className="label-caps text-xs text-muted-foreground hover:text-foreground">
          ← Vendors
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="display-tight text-3xl font-semibold">{vendor.businessName}</h1>
            <p className="text-sm text-muted-foreground">
              {[vendor.contactName, vendor.primaryCategory, vendor.phone]
                .filter(Boolean)
                .join(' · ') || 'No details yet'}
            </p>
            {vendor.website && (
              <a
                href={vendor.website.startsWith('http') ? vendor.website : `https://${vendor.website}`}
                target="_blank"
                rel="noreferrer"
                className="text-sm underline underline-offset-2"
              >
                {vendor.website}
              </a>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="size-3.5" />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setArchived.mutate(!vendor.archivedAt)}
            >
              {vendor.archivedAt ? 'Restore' : 'Archive'}
            </Button>
          </div>
        </div>
        {vendor.archivedAt && (
          <p className="text-xs text-muted-foreground">
            Archived — hidden from search, but every inquiry below is preserved.
          </p>
        )}
      </div>

      <EditVendorDialog
        vendor={vendor as unknown as EditableVendor}
        open={editing}
        onOpenChange={setEditing}
      />

      {/* ── Addresses ─────────────────────────────────────────────────────── */}
      <Card className="rounded-3xl bg-card p-5 shadow-ambient">
        <h2 className="display-tight mb-1 text-lg font-semibold">Email addresses</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Replies from any of these are matched to this vendor. Add the studio address when someone
          moves off their personal one — the old one keeps working.
        </p>

        <div className="space-y-1">
          {vendor.emails.map((e) => (
            <div key={e.id} className="flex items-center gap-3 rounded-2xl px-3 py-2 text-sm">
              {e.isPrimary && <Star className="size-3.5 fill-current text-primary" />}
              <span className="flex-1">{e.email}</span>
              <span className="text-xs text-muted-foreground">
                {e.isPrimary ? 'Primary — inquiries go here' : 'Also matched'}
              </span>
              {!e.isPrimary && (
                <button
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => removeEmail.mutate(e.id)}
                >
                  <Trash2 className="size-3.5" />
                </button>
              )}
            </div>
          ))}
          {vendor.emails.length === 0 && (
            <p className="px-3 py-2 text-sm text-muted-foreground">
              No addresses yet — this vendor can't be included in a batch until there is one.
            </p>
          )}
        </div>

        <div className="mt-3 flex gap-2">
          <Input
            className="h-9"
            type="email"
            placeholder="another@address.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newEmail.trim()) addEmail.mutate(newEmail.trim());
            }}
          />
          <Button
            variant="outline"
            size="sm"
            disabled={!newEmail.trim() || addEmail.isPending}
            onClick={() => addEmail.mutate(newEmail.trim())}
          >
            <Plus className="size-3.5" />
            Add
          </Button>
        </div>
      </Card>

      {/* ── History strip (PRD §F1) ───────────────────────────────────────── */}
      <Card className="rounded-3xl bg-card p-5 shadow-ambient">
        <h2 className="display-tight mb-4 text-lg font-semibold">
          History
          <span className="ml-2 text-sm font-normal text-muted-foreground">
            {vendor.history.length} {vendor.history.length === 1 ? 'wedding' : 'weddings'}
          </span>
        </h2>

        {vendor.history.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            You haven't inquired with them yet.
          </p>
        ) : (
          <div className="divide-y divide-border">
            {vendor.history.map((h) => (
              <Link
                key={h.inquiryId}
                to={`/inquiries/${h.inquiryId}`}
                className="flex flex-wrap items-center justify-between gap-3 py-3 transition-colors hover:bg-[var(--color-surface-container-high)]"
              >
                <div>
                  <p className="text-sm font-medium">{h.coupleNames}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(h.weddingDate)} · {h.category}
                    {h.sentAt && ` · inquired ${formatInstant(h.sentAt, { month: 'short', year: 'numeric' })}`}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {h.quoteAmount && (
                    <span className="text-sm font-medium whitespace-nowrap">
                      {formatQuote({
                        amount: h.quoteAmount,
                        amountHigh: h.quoteAmountHigh,
                        currency: h.quoteCurrency,
                      })}
                    </span>
                  )}
                  <StatusChip status={h.status as InquiryStatus} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </Card>

      {vendor.notes && (
        <Card className="rounded-3xl bg-card p-5 shadow-ambient">
          <h2 className="display-tight mb-2 text-lg font-semibold">Notes</h2>
          <p className="whitespace-pre-wrap text-sm">{vendor.notes}</p>
        </Card>
      )}
    </div>
  );
}
